import { Link } from 'react-router-dom';
import { CheckCircle } from 'lucide-react';
import { INVOLVEMENT_TIERS } from '../data/pricing';

// The headline pricing ladder — four involvement tiers (Blueprint/DIY free,
// Setup Session, Done-for-you). The own-vs-hosted choice only appears inside
// Done-for-you (on the quote / blueprint), never here.
const InvolvementTiers = ({
  heading = 'Pick how hands-on you want to be',
  subheading = 'Two of these are free. You only pay when you want us doing the work — and you always own what we build.',
}) => (
  <section id="pricing" className="py-20 sm:py-24">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="text-center mb-14">
        <h2 className="brand-header font-sans text-4xl font-extrabold sm:text-5xl">{heading}</h2>
        <p className="mt-4 max-w-3xl mx-auto text-xl text.body">{subheading}</p>
      </div>

      {/* Same-Day Launch: the fastest paid path. Static page in public/, so <a> not Link. */}
      <a
        href="/same-day-launch/"
        className="group mb-8 flex flex-col md:flex-row md:items-center gap-4 md:gap-8 rounded-2xl border-2 border-purple-600 bg-gradient-to-r from-purple-50 to-white p-6 shadow-lg transition-transform hover:-translate-y-1"
      >
        <div className="flex-1">
          <span className="inline-block rounded-full bg-purple-600 px-3 py-0.5 text-xs font-semibold text-white">Fastest · new</span>
          <h3 className="mt-3 text-2xl font-extrabold font-sans text.heading">Same-Day Launch</h3>
          <p className="mt-1 text.body">
            One 60-minute call. Your site goes live on your own domain before we hang up, with business email and every login in your name.
            Last one took 33 minutes.
          </p>
        </div>
        <div className="flex items-center gap-6 md:flex-col md:items-end md:gap-1">
          <div className="text-3xl font-extrabold text-purple-700">$497 <span className="text-base font-medium text.muted">flat</span></div>
          <span className="rounded-lg bg-purple-600 px-5 py-2.5 text-sm font-semibold text-white group-hover:bg-purple-700">See how it works</span>
        </div>
      </a>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {INVOLVEMENT_TIERS.map((tier) => (
          <div
            key={tier.key}
            className={`relative flex flex-col rounded-2xl bg-white p-6 shadow-lg transition-transform hover:-translate-y-1 ${
              tier.featured ? 'border-2 border-purple-600 ring-1 ring-purple-600/20' : 'border border-gray-200'
            }`}
          >
            {tier.featured && (
              <span className="absolute -top-3 left-6 rounded-full bg-purple-600 px-3 py-0.5 text-xs font-semibold text-white">
                Most popular
              </span>
            )}
            <h3 className="text-xl font-bold text.heading">{tier.name}</h3>
            <p className="text-sm text.muted">{tier.tagline}</p>
            <p className="mt-4 text-3xl font-extrabold text.heading">{tier.price}</p>
            <p className="mt-3 text-sm text.body flex-grow">{tier.description}</p>
            <ul className="mt-5 space-y-2">
              {tier.features.map((f) => (
                <li key={f} className="flex items-start gap-2 text-sm text.body">
                  <CheckCircle className="mt-0.5 h-4 w-4 flex-shrink-0 text-green-500" aria-hidden="true" />
                  {f}
                </li>
              ))}
            </ul>
            {tier.note && <p className="mt-4 text-xs font-medium text-purple-700">{tier.note}</p>}
            {/* Paid tiers point at the external Calendly free call (href);
                free tiers stay on in-app routes (to). */}
            {tier.cta.href ? (
              <a
                href={tier.cta.href}
                target="_blank"
                rel="noopener noreferrer"
                className={`mt-6 block rounded-lg px-4 py-3 text-center text-sm font-semibold transition-colors ${
                  tier.featured
                    ? 'bg-purple-600 text-white hover:bg-purple-700'
                    : 'border border-purple-600 text-purple-600 hover:bg-purple-50'
                }`}
              >
                {tier.cta.label}
              </a>
            ) : (
              <Link
                to={tier.cta.to}
                className={`mt-6 block rounded-lg px-4 py-3 text-center text-sm font-semibold transition-colors ${
                  tier.featured
                    ? 'bg-purple-600 text-white hover:bg-purple-700'
                    : 'border border-purple-600 text-purple-600 hover:bg-purple-50'
                }`}
              >
                {tier.cta.label}
              </Link>
            )}
          </div>
        ))}
      </div>

      <p className="mt-6 text-center text-sm text.muted">
        Done-for-you projects come two ways: <span className="font-medium text.body">own the code</span> (one-time)
        {' '}or <span className="font-medium text.body">hosted with us</span> (lower upfront + monthly care). You&apos;ll see both on your quote.
      </p>
    </div>
  </section>
);

export default InvolvementTiers;
